import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Typography,
} from "@material-ui/core";
import Menu from "../menu/menu";
import { confirmDeletion, getDeletionPreview } from "../../components/account/account-deletion-api";
import { isNativeCapacitorRuntime } from "../../services/nativeRuntime";
import { showManageSubscriptions } from "../../services/nativeSubscriptionManagement";
import { signOutAfterDeletion } from "../../utils/account-deletion-signout";
import "./delete-account.scss";

const SUPPORT_EMAIL = "support@medlearnity.com";
const POLICY_SECTION = "/privacy-policy#account-deletion";
const LEGAL_WEB_BASE = "https://qlearn.medlearnity.com";

const UNCONFIRMED_COPY =
  "We couldn't confirm whether your account was deleted. Check your connection and tap Delete my account again.";

const ERROR_COPY = {
  NOTHING_DELETED: "Nothing was deleted. We couldn't finish right now; please try again in a few minutes.",
  TRY_LATER: `We can't delete your account right now. Please try again in a few minutes, or email ${SUPPORT_EMAIL}.`,
  NETWORK_ERROR: UNCONFIRMED_COPY,
  UNCONFIRMED: UNCONFIRMED_COPY,
  SIGNED_OUT: "Your session ended, so nothing was deleted. Please sign in again and return to Delete account.",
  NO_SESSION: "Your session ended, so nothing was deleted. Please sign in again and return to Delete account.",
  REQUEST_FAILED: `Something went wrong and nothing was deleted. Please try again, or email ${SUPPORT_EMAIL}.`,
};

const STAFF_UNCONFIRMED_COPY =
  "We couldn't confirm that your request was sent. Check your connection and tap Request account deletion again.";

// Outcomes after which the deletion may have run on the server without us hearing back.
const UNCONFIRMED = new Set(["NETWORK_ERROR", "UNCONFIRMED"]);

const formatDay = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

// In the app the policy page has no menu or tab bar to get back with; open it in the in-app browser.
const openPolicy = async (event) => {
  if (!isNativeCapacitorRuntime()) return;
  event.preventDefault();
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url: `${LEGAL_WEB_BASE}${POLICY_SECTION}` });
  } catch (_) {
    window.location.assign(POLICY_SECTION);
  }
};

// Settings → Delete account (App Store Guideline 5.1.1(v); iOS ruling B1 §9). One screen: what
// is erased, what is kept and why, the App Store cancel prompt in the app, an explicit
// destructive confirm, then the API call, sign-out and the "account deleted" screen.
export default function DeleteAccountPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const native = isNativeCapacitorRuntime();
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [understood, setUnderstood] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [requestFiled, setRequestFiled] = useState(null);
  const [managing, setManaging] = useState(false);
  const mounted = useRef(true);
  const alertRef = useRef(null);
  // Set when a confirm may have reached the server without us hearing back. The server ends every
  // session as it deletes, so a 401 on the next attempt means the account is already gone.
  const mayHaveDeleted = useRef(false);

  const loadPreview = useCallback(() => {
    setLoadError(null);
    return getDeletionPreview().then((result) => {
      if (!mounted.current) return;
      if (result.ok) {
        setPreview(result.data);
        if (result.data.openRequestDueBy) setRequestFiled({ dueBy: result.data.openRequestDueBy });
      } else {
        setLoadError(result.code);
      }
    });
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (!window.localStorage.getItem("token")) {
      history.replace("/login?next=/user-settings");
      return undefined;
    }
    loadPreview();
    return () => {
      mounted.current = false;
    };
  }, [history, loadPreview]);

  const staff = preview?.mode === "STAFF_REQUEST";

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const result = await confirmDeletion();
    // Only a student account is deleted from here; a staff account only files a request.
    const deleted = (result.ok && result.data.outcome === "DELETED")
      || (!staff && !result.ok && result.code === "SIGNED_OUT" && mayHaveDeleted.current);
    if (deleted) {
      // Even if the person left this page meanwhile: the account is gone, so forget it here too.
      await signOutAfterDeletion({ dispatch, history });
      return;
    }
    if (!staff && !result.ok && UNCONFIRMED.has(result.code)) mayHaveDeleted.current = true;
    if (!mounted.current) return;
    setSubmitting(false);
    if (result.ok && result.data.outcome === "REQUEST_FILED") {
      setRequestFiled({ dueBy: result.data.requestDueBy || null });
      return;
    }
    let message;
    if (result.ok) {
      message = ERROR_COPY.REQUEST_FAILED;
    } else if (staff && UNCONFIRMED.has(result.code)) {
      message = STAFF_UNCONFIRMED_COPY;
    } else if (mayHaveDeleted.current && (result.code === "TRY_LATER" || result.code === "NO_SESSION")) {
      // A claim from the earlier attempt is still running, or its deletion already cleared the session.
      message = UNCONFIRMED_COPY;
    } else {
      message = ERROR_COPY[result.code] || ERROR_COPY.REQUEST_FAILED;
    }
    setError(message);
    // The button was disabled during the request; keep focus somewhere meaningful.
    setTimeout(() => alertRef.current && alertRef.current.focus(), 0);
  };

  const onManageSubscription = async () => {
    if (managing) return;
    setManaging(true);
    try {
      await showManageSubscriptions();
    } finally {
      if (mounted.current) {
        setManaging(false);
        // The sheet may have cancelled the subscription; show the current state.
        loadPreview();
      }
    }
  };

  const showAppStore = native || preview?.appStoreSubscriptionActive;
  const webEnds = formatDay(preview?.webSubscriptionEndsAt);

  return (
    // settings-container: a Settings subpage, so it shares the bottom-tab-bar reserve (App.scss).
    <Box className="delete-account-container settings-container">
      <Menu />
      <Box className="delete-account-content">
        <Paper elevation={0} className="delete-account-paper" component="section" aria-labelledby="delete-account-title">
          <Typography variant="h5" component="h1" id="delete-account-title" gutterBottom>
            Delete your account
          </Typography>

          {!preview && !loadError && (
            <Box className="delete-account-loading" role="status" aria-live="polite">
              <CircularProgress size={28} aria-label="Loading" />
            </Box>
          )}

          {loadError && !preview && (
            <>
              <Typography role="alert" className="delete-account-error">
                {loadError === "SIGNED_OUT" || loadError === "NO_SESSION"
                  ? "Please sign in again to delete your account."
                  : "We couldn't load this page. Please try again."}
              </Typography>
              <Box className="delete-account-actions">
                {loadError === "SIGNED_OUT" || loadError === "NO_SESSION" ? (
                  <Button component={RouterLink} to="/login?next=/user-settings" variant="contained" color="primary"
                    className="delete-account-primary">
                    Sign in
                  </Button>
                ) : (
                  <Button variant="contained" color="primary" className="delete-account-primary" onClick={loadPreview}>
                    Try again
                  </Button>
                )}
                <Button component={RouterLink} to="/user-settings" className="delete-account-cancel">
                  Back to settings
                </Button>
              </Box>
            </>
          )}

          {preview && (staff || requestFiled) && (
            <StaffRequest
              requestFiled={requestFiled}
              staffDays={preview.staffRequestDays}
              submitting={submitting}
              error={error}
              alertRef={alertRef}
              onRequest={submit}
            />
          )}

          {preview && !staff && !requestFiled && (
            <>
              <Typography className="delete-account-lead">
                This permanently deletes your QLearn account. You can't undo it.
              </Typography>

              {showAppStore && (
                <Box
                  className={`delete-account-callout${preview.appStoreSubscriptionActive ? " delete-account-callout--warn" : ""}`}
                  data-testid="delete-account-app-store"
                >
                  <Typography component="h2" variant="subtitle1" className="delete-account-callout-title">
                    {preview.appStoreSubscriptionActive
                      ? "Cancel your App Store subscription first"
                      : "Subscriptions bought in the App Store"}
                  </Typography>
                  <Typography variant="body2">
                    Deleting your account doesn't cancel a subscription bought in the App Store. Apple keeps
                    billing it until you cancel it.
                  </Typography>
                  {native && (
                    <Button
                      variant="outlined"
                      className="delete-account-secondary"
                      disabled={managing}
                      onClick={onManageSubscription}
                    >
                      Manage App Store subscription
                    </Button>
                  )}
                  {!native && preview.appStoreSubscriptionActive && (
                    <Typography variant="body2">
                      To cancel it, open Settings on your iPhone, tap your name, then Subscriptions.
                    </Typography>
                  )}
                </Box>
              )}

              <Typography component="h2" variant="subtitle1" className="delete-account-heading">
                What we delete
              </Typography>
              <ul className="delete-account-list">
                <li>Your profile, email address, phone number and sign-in methods</li>
                <li>Your quizzes, answers, scores, bookmarks, notes and comments</li>
                <li>Your settings, tutor links, referral code and pending referral rewards</li>
              </ul>
              <Typography variant="body2" className="delete-account-note">
                We also delete your Mixpanel (analytics) profile and ask Mixpanel and RevenueCat (App Store
                purchases) to delete anything else they hold about you, and, if you signed in with Apple, we
                revoke QLearn's access to your Apple ID.
              </Typography>

              <Typography component="h2" variant="subtitle1" className="delete-account-heading">
                Subscriptions and refunds
              </Typography>
              <ul className="delete-account-list">
                <li>
                  {preview.webSubscriptionActive && webEnds
                    ? `Your QLearn web subscription ends as soon as you delete your account, even though it's paid through ${webEnds}. `
                    : "A QLearn web subscription ends as soon as you delete your account. "}
                  There's no automatic refund. To ask about one, email{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </li>
                {showAppStore && (
                  <li>
                    If you sign up again and tap Restore Purchases, an App Store subscription that is still billing
                    moves to your new account.
                  </li>
                )}
              </ul>

              <Typography component="h2" variant="subtitle1" className="delete-account-heading">
                What we keep, and why
              </Typography>
              <ul className="delete-account-list">
                <li>
                  Records of your payments, subscriptions and refunds, because tax and accounting law requires
                  them. We remove your name and account from our copy; Stripe, our payment processor, keeps its
                  own invoice records.
                </li>
                <li>
                  Copies stay in our encrypted database backups for up to 8 days, and in a few disaster-recovery
                  snapshots until we retire them. See the{" "}
                  <a href={POLICY_SECTION} onClick={openPolicy}>Privacy Policy</a>.
                </li>
              </ul>

              <Typography className="delete-account-note">
                If you sign in again later, you'll start with a new, empty account.
              </Typography>

              <FormControlLabel
                className="delete-account-check"
                control={
                  <Checkbox
                    checked={understood}
                    onChange={(event) => setUnderstood(event.target.checked)}
                    color="primary"
                    inputProps={{ "data-testid": "delete-account-understood" }}
                  />
                }
                label={
                  <span className="delete-account-check-label">
                    I understand my account and data will be permanently deleted.
                  </span>
                }
              />

              {error && (
                <Typography role="alert" className="delete-account-error" tabIndex={-1} ref={alertRef}>
                  {error}
                </Typography>
              )}
              {submitting && (
                <Typography role="status" className="delete-account-note" data-testid="delete-account-progress">
                  Deleting your account… this can take up to 30 seconds. Please keep this screen open.
                </Typography>
              )}

              <Box className="delete-account-actions">
                <Button
                  variant="contained"
                  className="delete-account-destructive"
                  disabled={!understood || submitting}
                  aria-describedby={!understood ? "delete-account-why-disabled" : undefined}
                  onClick={() => {
                    // submit() settles every outcome itself (it never rejects), so nothing awaits it.
                    if (understood && !submitting) void submit();
                  }}
                  data-testid="delete-account-confirm"
                >
                  {submitting ? "Deleting…" : "Delete my account"}
                </Button>
                {!submitting && (
                  // Once it has started, a deletion can't be stopped from here.
                  <Button component={RouterLink} to="/user-settings" className="delete-account-cancel">
                    Cancel
                  </Button>
                )}
              </Box>
              {!understood && (
                <span id="delete-account-why-disabled" className="delete-account-visually-hidden">
                  Tick the box above to enable this button.
                </span>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

function StaffRequest({ requestFiled, staffDays, submitting, error, alertRef, onRequest }) {
  if (requestFiled) {
    const day = formatDay(requestFiled.dueBy);
    return (
      <Box role="status" data-testid="delete-account-request-filed">
        <Typography className="delete-account-lead">We received your request.</Typography>
        <Typography>
          {day
            ? `Our team will delete your account by ${day} and email the address on your account when it's done.`
            : `Our team will delete your account within ${staffDays || 7} days and email the address on your account when it's done.`}
          {" "}You don't need to do anything else. Changed your mind? Email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> before then.
        </Typography>
        <Button component={RouterLink} to="/user-settings" variant="outlined" className="delete-account-secondary">
          Back to settings
        </Button>
      </Box>
    );
  }
  return (
    <>
      <Typography className="delete-account-lead">
        Tutor and administrator accounts are deleted by our team.
      </Typography>
      <Typography>
        We'll delete your account and its personal data within {staffDays || 7} days, and email the address on your
        account when it's done. Records of payments and of the questions you reviewed or published are kept without
        your name.
      </Typography>
      {error && (
        <Typography role="alert" className="delete-account-error" tabIndex={-1} ref={alertRef}>
          {error}
        </Typography>
      )}
      <Box className="delete-account-actions">
        <Button
          variant="contained"
          className="delete-account-destructive"
          disabled={submitting}
          onClick={() => {
            // onRequest settles every outcome itself (it never rejects), so nothing awaits it.
            void onRequest();
          }}
          data-testid="delete-account-request"
        >
          {submitting ? "Sending…" : "Request account deletion"}
        </Button>
        <Button component={RouterLink} to="/user-settings" className="delete-account-cancel">
          Cancel
        </Button>
      </Box>
    </>
  );
}
